import { select } from 'd3-selection';
import { geoOrthographic, geoPath, geoGraticule } from 'd3-geo';
import 'd3-transition';

// CNN disputed-borders basemap (Natural Earth 50m, edited to CNN guidelines,
// clipped to a Europe bbox). Rendered per the set's README stacking order:
// disputed-lines (dotted) over country-lines (solid) over country-polys (fill).
import borders from './data/cnn-borders.json';
import stepsData from './data/data.json';
import meta from './data/meta.json';
import hatImg from './images/berezovska-hat.png';
import berezovskaImg from './images/berezovska.png';
import monacoBombImg from './images/monaco-bomb.png';
import homeImg from './images/berezovska-home.jpg';
import redNoticeImg from './images/interpol-red-notice.png';
import graveImg from './images/berezovska-grave.jpg';
import locatorImg from './images/locator.png';
import './styles/graphic.scss';

const RED = '#b3230f';
const SPHERE = { type: 'Sphere' };

// Maps the sheet's `image` filename to the webpack-resolved asset URL. Add new
// entries here (and a matching `import`) when the sheet references more images.
const IMAGES = {
  'berezovska-hat.png': hatImg,
  'berezovska.png': berezovskaImg,
  'monaco-bomb.png': monacoBombImg,
  'berezovska-home.jpg': homeImg,
  'interpol-red-notice.png': redNoticeImg,
  'berezovska-grave.jpg': graveImg,
};

// Locator detail map for the Monaco -> Beausoleil hop. The two locations are
// ~300m apart, so they're a single pixel on the Europe-wide map; this inset
// (a supplied PNG) shows the detail. Shown only on the "flees" step.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Per-marker label placement (dx/dy in px from the projected point, plus text
// anchor). Pure layout — hand-tuned so the labels don't collide at this scale.
// Keyed by the stable location slug (see slugFor).
const POINT_STYLE = {
  hofheim: {
    name: 'Hofheim, Germany', lines: ['Hofheim,', 'Germany'], dx: 0, dy: -22, anchor: 'middle',
  },
  frankfurt: {
    name: 'Frankfurt, Germany', lines: ['Frankfurt,', 'Germany'], dx: 0, dy: -22, anchor: 'middle',
  },
  monaco: {
    name: 'Monaco', dx: -2, dy: 14, anchor: 'middle',
  },
  // Beausoleil sits ~300m from Monaco (same pixel at this zoom), so the label
  // is placed above the shared dot — with Monaco's label below — to convey the
  // move into France on the "flees" step without an invisible sub-pixel line.
  beausoleil: {
    name: 'Beausoleil, France', lines: ['Beausoleil,', 'France'], dx: 0, dy: -22, anchor: 'middle',
  },
  border: {
    name: 'Border crossing', dx: 0, dy: -10, anchor: 'middle',
  },
  horodyshche: {
    // Below the dot so its label clears Yuriv's (which sits just NE and labels
    // above); the space south of Horodyshche is open.
    name: 'Horodyshche, Ukraine', lines: ['Horodyshche,', 'Ukraine'], dx: 0, dy: 16, anchor: 'middle',
  },
  yuriv: {
    name: 'Yuriv, Ukraine', lines: ['Yuriv,', 'Ukraine'], dx: 0, dy: -22, anchor: 'middle',
  },
};

// Always-visible geographic context — not timeline stops, so they live here as
// design constants rather than in the sheet.
const KYIV = { c: [30.5234, 50.4501], name: 'KYIV' };
const COUNTRIES = [
  { c: [2.5, 46.6], n: 'FRANCE' },
  { c: [10.4, 52.6], n: 'GERMANY' },
  { c: [19.4, 52.0], n: 'POLAND' },
  { c: [34.5, 48.6], n: 'UKRAINE' },
  { c: [12.4, 42.9], n: 'ITALY' },
];


// Legs drawn dashed ("reconstructed"). Per the source footnote, everything up
// to and including Frankfurt is confirmed; the Frankfurt → Ukraine legs and the
// final leg to the grave are reconstructed from investigators' accounts.
const DASHED_SEGMENTS = new Set([
  'frankfurt>border',
  'border>horodyshche',
  'horodyshche>yuriv',
]);

// All route legs render as dotted lines (round caps + zero-length dashes).
const ROUTE_DOTS = '0.1 4';

// Points kept as route waypoints (so the line still bends through them) but not
// drawn as labeled markers. The unconfirmed border crossing is a waypoint only;
// a step focused on it falls through to its route's final destination.
const HIDDEN_POINTS = new Set(['border']);

// Per-step map annotations, keyed by step number. `emphasize` bolds extra point
// labels beyond the flashing focus; `accent` recolors the focus dot/pulse (e.g.
// blue for an investigative, non-movement step); `fadeRoute` dims the whole red
// route back; `callout` draws a leader-lined note beside a point (dx/dy/anchor
// position it relative to that point's pixel).
const STEP_NOTES = {
  // The Frankfurt "Interpol Red Notice" step is investigative (not a movement),
  // so its dot goes blue and the route/other dots fade back.
  6: {
    emphasize: ['hofheim', 'frankfurt'],
    accent: '#2b6cb0',
    fadeRoute: true,
  },
};

// Map a free-text location string from the sheet onto a stable slug so edits to
// the display name (e.g. adding an address) don't break the geometry lookups.
function slugFor(location) {
  const s = String(location).toLowerCase();
  if (s.includes('hofheim')) return 'hofheim';
  if (s.includes('monaco')) return 'monaco';
  if (s.includes('beausoleil')) return 'beausoleil';
  if (s.includes('frankfurt')) return 'frankfurt';
  if (s.includes('border')) return 'border';
  if (s.includes('horodyshche')) return 'horodyshche';
  if (s.includes('yuriv')) return 'yuriv';
  if (s.includes('kyiv') || s.includes('kiev')) return 'kyiv';
  return s.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function parseDate(raw) {
  // Format is M/D/YY, where D may be a single day ("26") or a range ("26-27").
  const [m, d, y] = String(raw).split('/');
  const month = MONTHS[parseInt(m, 10) - 1];
  const day = String(d || '').trim();
  const yNum = parseInt(y, 10);
  const year = yNum < 100 ? 2000 + yNum : yNum;
  return { label: `${month} ${day}`, year: String(year) };
}

function parseChain(routeToHere) {
  if (!routeToHere) return [];
  return String(routeToHere)
    .split(/→|->/)
    .map(t => slugFor(t.trim()))
    .filter(Boolean);
}

// Turn the flat sheet rows into everything the renderer needs: a de-duplicated
// set of points (with coordinates + label styling), the ordered list of route
// segments (each tagged with the step it first appears at), and per-step derived
// state (what's visible, what's focused, and the card copy).
function buildModel(rows) {
  const points = {};
  const addPoint = (slug, c, name) => {
    if (points[slug] || !c || Number.isNaN(c[0]) || Number.isNaN(c[1])) return;
    points[slug] = {
      slug,
      c,
      style: POINT_STYLE[slug] || {
        name: name || slug, dx: 0, dy: -10, anchor: 'middle',
      },
    };
  };

  rows.forEach((r) => {
    addPoint(slugFor(r.location), [Number(r.longitude), Number(r.latitude)], r.location);
  });

  const segments = [];
  const seenSeg = new Set();
  const steps = [];
  const shown = new Set();

  rows.forEach((r, i) => {
    const chain = parseChain(r.route_to_here);
    let focus = slugFor(r.location);
    // A step whose location is a hidden waypoint (e.g. the unconfirmed border
    // crossing) flashes its route's final destination instead.
    if (HIDDEN_POINTS.has(focus) && chain.length) focus = chain[chain.length - 1];

    // Hidden waypoints (e.g. the unconfirmed border crossing) are dropped from
    // the drawn line so the route arcs directly between visible points rather
    // than dipping through them.
    const drawChain = chain.filter(s => !HIDDEN_POINTS.has(s) && points[s]);
    for (let j = 0; j < drawChain.length - 1; j += 1) {
      const a = drawChain[j];
      const b = drawChain[j + 1];
      const key = `${a}>${b}`;
      if (!seenSeg.has(key)) {
        seenSeg.add(key);
        segments.push({
          a, b, key, dash: DASHED_SEGMENTS.has(key), revealStep: i,
        });
      }
    }

    shown.add(focus);
    chain.forEach(s => shown.add(s));

    const { label, year } = parseDate(r.date);
    steps.push({
      focus,
      stepNum: Number(r.step),
      date: label,
      year,
      text: r.event,
      image: r.image ? IMAGES[r.image] : null,
      imageName: r.image || null,
      imageSource: (r.source || '').trim(),
      show: new Set(shown),
    });
  });

  return { points, segments, steps };
}

export default function initGraphic(mount) {
  const { points, segments, steps } = buildModel(stepsData);

  // --- Build the static DOM shell -----------------------------------------
  const root = select(mount);
  root.html('');
  const wrap = root.append('div').attr('class', 'berez');

  const header = wrap.append('header').attr('class', 'berez__header');
  header.append('p').attr('class', 'berez__kicker').text(meta.kicker || '');
  header.append('h1').attr('class', 'berez__hed').text(meta.headline || '');
  if (meta.subhed) header.append('p').attr('class', 'berez__dek').text(meta.subhed);

  const legend = wrap.append('div').attr('class', 'berez__legend');
  const legConfirmed = legend.append('span').attr('class', 'berez__legend-item');
  legConfirmed.html(`<svg width="26" height="4"><line x1="0" y1="2" x2="26" y2="2" stroke="${RED}" stroke-width="2"/></svg>`);
  legConfirmed.append('span').text('Confirmed route');
  const legRecon = legend.append('span').attr('class', 'berez__legend-item');
  legRecon.html(`<svg width="26" height="4"><line x1="0" y1="2" x2="26" y2="2" stroke="${RED}" stroke-width="2" stroke-dasharray="3 4"/></svg>`);
  legRecon.append('span').text('Reconstructed');

  const figure = wrap
    .append('figure')
    .attr('class', 'berez__map')
    .attr('aria-label', "Orthographic map of Europe tracing Berezovska's route");

  const card = wrap.append('div').attr('class', 'berez__card');
  const cDate = card.append('p').attr('class', 'berez__c-date fade');
  const cText = card.append('p').attr('class', 'berez__c-text fade');

  const nav = wrap.append('nav').attr('class', 'berez__nav').attr('aria-label', 'Timeline steps');
  const chevron = d => `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
  const prevBtn = nav.append('button').attr('class', 'berez__btn berez__prev').attr('aria-label', 'Previous event').html(chevron('M15 5 L8 12 L15 19'));
  const rail = nav.append('div').attr('class', 'berez__rail');
  const nextBtn = nav.append('button').attr('class', 'berez__btn berez__next').attr('aria-label', 'Next event').html(chevron('M9 5 L16 12 L9 19'));

  const dots = steps.map((s, i) => rail
    .append('button')
    .attr('class', 'berez__dot')
    .attr('aria-label', `${s.date} ${s.year}`)
    // eslint-disable-next-line no-use-before-define
    .on('click', () => update(i))
    .node());

  // Footnote / source / credit render via the rig's boilerplate #footer in
  // public/index.html, populated from the Google Sheet (see config.*.js).

  // --- Map rendering -------------------------------------------------------
  let cur = 0;
  let els = {
    segs: [], pts: {}, notes: {}, routeG: null, kyiv: null,
  };

  function renderMap() {
    figure.html('');
    const W = figure.node().clientWidth || 340;
    // A tall, large orthographic globe clipped to a short viewBox: only the
    // upper cap (Europe, down to the tip of North Africa) shows through, with
    // the sphere's limb curving across the top. The rest of the globe is
    // cropped away by the svg viewBox (overflow: hidden).
    const H = Math.round(W * 0.78);
    const R = W * 2.1;
    // Vertical offset of the globe centre. Larger => the visible cap (Europe)
    // sits lower in the frame, pushing the tip of Italy toward the bottom crop
    // and opening empty sea at the top for the hanging photo insets.
    const CY = R + W * 0.22;

    const proj = geoOrthographic()
      .rotate([-19, 8])
      .scale(R)
      .translate([W / 2, CY])
      .clipAngle(90)
      .precision(0.3);
    const path = geoPath(proj);

    const svg = figure
      .append('svg')
      .attr('viewBox', `0 0 ${W} ${H}`)
      .attr('width', W)
      .attr('height', H);

    const defs = svg.append('defs');
    const grad = defs
      .append('radialGradient')
      .attr('id', 'berez-limb')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('cx', W / 2)
      .attr('cy', CY)
      .attr('r', R);
    grad.append('stop').attr('offset', '90%').attr('stop-color', '#000').attr('stop-opacity', 0);
    grad.append('stop').attr('offset', '100%').attr('stop-color', '#3a3f47').attr('stop-opacity', 0.1);

    svg.append('path').datum(SPHERE).attr('d', path).attr('fill', '#fdfdfc');
    svg
      .append('path')
      .datum(geoGraticule().step([5, 5]))
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', '#eceae5')
      .attr('stroke-width', 0.6);

    // Fill only, no stroke on the polygons (per CNN README).
    svg.append('path').datum(borders.polys).attr('d', path).attr('fill', '#e9e6df');
    // Internationally-recognized boundaries: solid lines.
    svg
      .append('path')
      .datum(borders.lines)
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.9);
    // Disputed boundaries: dotted lines, drawn above the solid ones.
    svg
      .append('path')
      .datum(borders.disputed)
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', '#b6b2a8')
      .attr('stroke-width', 0.9)
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', '0.1,2.6');
    svg
      .append('path')
      .datum(SPHERE)
      .attr('d', path)
      .attr('fill', 'url(#berez-limb)')
      .attr('stroke', '#c8c5bd')
      .attr('stroke-width', 1);

    const cty = svg.append('g');
    COUNTRIES.forEach((d) => {
      const p = proj(d.c);
      if (!p) return;
      cty
        .append('text')
        .attr('x', p[0])
        .attr('y', p[1])
        .attr('text-anchor', 'middle')
        .attr('font-size', 8.5)
        .attr('letter-spacing', '0.16em')
        .attr('fill', '#b6b2a8')
        .attr('font-weight', 500)
        .text(d.n);
    });

    let kyivG = null;
    const kp = proj(KYIV.c);
    if (kp) {
      // Hidden until the final step (see update()).
      kyivG = svg.append('g').style('opacity', 0);
      kyivG
        .append('circle')
        .attr('cx', kp[0])
        .attr('cy', kp[1])
        .attr('r', 2.6)
        .attr('fill', '#fff')
        .attr('stroke', '#8a8a8a')
        .attr('stroke-width', 1.1);
      kyivG
        .append('text')
        .attr('x', kp[0] + 6)
        .attr('y', kp[1] + 3)
        .attr('text-anchor', 'start')
        .attr('font-size', 8.5)
        .attr('letter-spacing', '0.1em')
        .attr('fill', '#8a8a8a')
        .attr('font-weight', 600)
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        .attr('paint-order', 'stroke')
        .text(KYIV.name);
    }

    els = {
      segs: [], pts: {}, notes: {}, routeG: null, kyiv: kyivG,
    };

    // All route legs live in one group so a step can fade the whole route back
    // (e.g. step 7, which describes the investigation rather than a movement).
    const routeG = svg.append('g');
    els.routeG = routeG;

    // Bow each leg into a gentle arc (quadratic Bézier, control point offset
    // perpendicular to the chord) so the route reads as an implied connection
    // rather than an exact path on the ground.
    const arcPath = (ca, cb) => {
      const pa = proj(ca);
      const pb = proj(cb);
      if (!pa || !pb) return null;
      const dx = pb[0] - pa[0];
      const dy = pb[1] - pa[1];
      const dr = Math.sqrt(dx * dx + dy * dy);
      if (dr < 1) return `M${pa[0]},${pa[1]} L${pb[0]},${pb[1]}`;
      const bow = 0.16; // fraction of chord length the arc bulges
      const cx = (pa[0] + pb[0]) / 2 + (dy / dr) * dr * bow;
      const cy = (pa[1] + pb[1]) / 2 - (dx / dr) * dr * bow;
      return `M${pa[0]},${pa[1]} Q${cx},${cy} ${pb[0]},${pb[1]}`;
    };

    els.segs = segments.map((s) => {
      const sel = routeG
        .append('path')
        .attr('d', arcPath(points[s.a].c, points[s.b].c))
        .attr('fill', 'none')
        .attr('stroke', RED)
        .attr('stroke-width', 2.4)
        .attr('stroke-linecap', 'round')
        .attr('stroke-dasharray', ROUTE_DOTS)
        .style('opacity', 0);
      return { sel, reveal: s.revealStep };
    });

    const pg = svg.append('g');
    Object.values(points).forEach((d) => {
      if (d.slug === 'kyiv' || HIDDEN_POINTS.has(d.slug)) return;
      const p = proj(d.c);
      const g = pg.append('g').style('opacity', 0);
      const pulse = g
        .append('circle')
        .attr('class', 'berez__pulse')
        .attr('cx', p[0])
        .attr('cy', p[1])
        .attr('r', 4)
        .attr('fill', RED)
        .style('display', 'none');
      const dot = g
        .append('circle')
        .attr('cx', p[0])
        .attr('cy', p[1])
        .attr('r', 3.4)
        .attr('fill', '#333')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.3);
      const lbl = g
        .append('text')
        .attr('x', p[0] + d.style.dx)
        .attr('y', p[1] + d.style.dy)
        .attr('text-anchor', d.style.anchor)
        .attr('font-size', 10)
        .attr('font-weight', 600)
        .attr('fill', '#666')
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        .attr('paint-order', 'stroke');
      if (d.style.lines) {
        d.style.lines.forEach((ln, li) => {
          lbl.append('tspan').attr('x', p[0] + d.style.dx).attr('dy', li === 0 ? 0 : 11).text(ln);
        });
      } else {
        lbl.text(d.style.name);
      }
      els.pts[d.slug] = {
        g, dot, lbl, pulse,
      };
    });

    // Step-specific map callouts (leader line + wrapped note), hidden until the
    // matching step; see STEP_NOTES.
    Object.keys(STEP_NOTES).forEach((k) => {
      const co = STEP_NOTES[k].callout;
      if (!co || !points[co.slug]) return;
      const p = proj(points[co.slug].c);
      const tx = p[0] + co.dx;
      const ty = p[1] + co.dy;
      const g = svg.append('g').attr('class', 'berez__callout').style('opacity', 0);
      g
        .append('path')
        .attr('d', `M${p[0]},${p[1]} L${tx},${ty - 9}`)
        .attr('fill', 'none')
        .attr('stroke', '#9a968c')
        .attr('stroke-width', 0.8);
      const t = g
        .append('text')
        .attr('text-anchor', co.anchor)
        .attr('x', tx)
        .attr('y', ty)
        .attr('font-size', 8.5)
        .attr('font-weight', 500)
        .attr('fill', '#333')
        .attr('stroke', '#fff')
        .attr('stroke-width', 2.6)
        .attr('paint-order', 'stroke');
      co.lines.forEach((ln, li) => {
        t.append('tspan').attr('x', tx).attr('dy', li === 0 ? 0 : 11).text(ln);
      });
      els.notes[k] = g;
    });

    // Step-specific inset image + caption, pinned to the map's top-right.
    const inset = figure.append('figure').attr('class', 'berez__inset');
    const insetTitle = inset.append('figcaption').attr('class', 'berez__inset-title');
    const insetImg = inset.append('img').attr('class', 'berez__inset-img').attr('alt', '');
    const insetCap = inset.append('figcaption').attr('class', 'berez__inset-cap');
    els.inset = {
      box: inset, img: insetImg, title: insetTitle, cap: insetCap,
    };

    // Locator detail (Monaco -> Beausoleil), same top-right slot.
    els.locator = figure.append('figure').attr('class', 'berez__locator');
    els.locator
      .append('img')
      .attr('class', 'berez__locator-img')
      .attr('src', locatorImg)
      .attr('alt', 'Detail map showing Monaco and neighboring Beausoleil, France');
  }

  // --- Step transitions ----------------------------------------------------
  function update(i, animate = true) {
    const st = steps[i];
    cur = i;

    els.segs.forEach((seg) => {
      const { sel, reveal } = seg;
      const on = reveal <= i;
      if (on && Number(sel.style('opacity')) < 1) {
        // Dotted legs fade in — animating a dash offset would fight the pattern.
        sel.interrupt().attr('stroke-dasharray', ROUTE_DOTS).attr('stroke-dashoffset', null);
        if (animate) sel.style('opacity', 0).transition().duration(500).style('opacity', 1);
        else sel.style('opacity', 1);
      } else if (!on) {
        sel.interrupt().style('opacity', 0).attr('stroke-dasharray', ROUTE_DOTS).attr('stroke-dashoffset', null);
      }
    });

    const stepNote = STEP_NOTES[st.stepNum];
    const emphasized = stepNote && stepNote.emphasize ? new Set(stepNote.emphasize) : null;
    const accent = stepNote && stepNote.accent ? stepNote.accent : RED;
    const dimDots = Boolean(stepNote && stepNote.fadeRoute);

    // Dim the whole route back on non-movement steps (e.g. the investigation).
    if (els.routeG) {
      els.routeG.interrupt();
      const routeOpacity = stepNote && stepNote.fadeRoute ? 0.18 : 1;
      if (animate) els.routeG.transition().duration(400).style('opacity', routeOpacity);
      else els.routeG.style('opacity', routeOpacity);
    }

    Object.entries(els.pts).forEach(([slug, o]) => {
      const on = st.show.has(slug);
      const isFocus = slug === st.focus;
      // Focus flashes (accent dot + pulse); emphasized labels just bold up.
      const isBold = isFocus || (emphasized && emphasized.has(slug));
      o.g.interrupt();
      if (animate) o.g.transition().duration(350).style('opacity', on ? 1 : 0);
      else o.g.style('opacity', on ? 1 : 0);
      o.dot.attr('fill', isFocus ? accent : '#333').attr('r', isFocus ? 4 : 3.4);
      o.lbl.attr('fill', isBold ? '#121212' : '#adaaa1').attr('font-weight', isBold ? 700 : 500);
      o.pulse.attr('fill', accent).style('display', isFocus ? null : 'none');
      // Past locations (shown, but not the current focus) fade back so the
      // current location reads first — like the dotted route behind it. The
      // investigative step fades them further still.
      let dotOpacity = 1;
      if (!isFocus) dotOpacity = dimDots ? 0.2 : 0.4;
      o.dot.interrupt();
      if (animate) o.dot.transition().duration(350).style('opacity', dotOpacity);
      else o.dot.style('opacity', dotOpacity);
    });

    Object.entries(els.notes).forEach(([k, g]) => {
      g.interrupt();
      const showNote = Number(k) === st.stepNum;
      if (animate) g.transition().duration(300).style('opacity', showNote ? 1 : 0);
      else g.style('opacity', showNote ? 1 : 0);
    });

    // Kyiv is called out from the July 3 step (accomplices met near Kyiv)
    // through the final steps.
    if (els.kyiv) {
      els.kyiv.interrupt();
      const showKyiv = i >= steps.length - 3;
      if (animate) els.kyiv.transition().duration(300).style('opacity', showKyiv ? 1 : 0);
      else els.kyiv.style('opacity', showKyiv ? 1 : 0);
    }

    if (els.inset) {
      const hasImg = Boolean(st.image);
      els.inset.box.classed('is-visible', hasImg);
      // The home photo renders a bit smaller than the rest (its `top` is nudged
      // to keep the frame's bottom edge aligned with the other insets).
      els.inset.box.classed('is-home', st.imageName === 'berezovska-home.jpg');
      if (hasImg) {
        els.inset.img.attr('src', st.image).attr('alt', st.text || '');
        const title = st.imageName === 'berezovska-home.jpg' ? 'Berezovska’s family home' : '';
        els.inset.title.text(title);
        els.inset.title.style('display', title ? null : 'none');
        els.inset.cap.text(st.imageSource ? `Source: ${st.imageSource}` : '');
        els.inset.cap.style('display', st.imageSource ? null : 'none');
      }
    }

    // The Monaco -> Beausoleil hop is sub-pixel on the main map, so show the
    // schematic locator detail on that step instead.
    if (els.locator) els.locator.classed('is-visible', st.focus === 'beausoleil');

    const setTxt = () => {
      cDate.html(`${st.date}, <span class="yr">${st.year}</span>`);
      cText.text(st.text);
      cDate.style('opacity', 1);
      cText.style('opacity', 1);
    };
    if (animate) {
      cDate.style('opacity', 0);
      cText.style('opacity', 0);
      setTimeout(setTxt, 180);
    } else {
      setTxt();
    }

    prevBtn.node().disabled = i === 0;
    nextBtn.node().disabled = i === steps.length - 1;
    dots.forEach((d, j) => {
      d.classList.toggle('on', j === i);
      d.classList.toggle('done', j < i);
    });
  }

  // Pin the card to the tallest entry's height (at the current width) so the
  // overall graphic height stays fixed as readers step through the timeline.
  // Recomputed on resize since wrapping (and thus the max) changes with width.
  function lockCardHeight() {
    const node = card.node();
    card.style('height', 'auto').style('min-height', '0');
    let maxH = 0;
    steps.forEach((s) => {
      cDate.html(`${s.date}, <span class="yr">${s.year}</span>`);
      cText.text(s.text);
      const h = node.offsetHeight;
      if (h > maxH) maxH = h;
    });
    card.style('height', `${Math.ceil(maxH)}px`);
    const st = steps[cur];
    cDate.html(`${st.date}, <span class="yr">${st.year}</span>`);
    cText.text(st.text);
  }

  // --- Wire up controls ----------------------------------------------------
  prevBtn.on('click', () => cur > 0 && update(cur - 1));
  nextBtn.on('click', () => cur < steps.length - 1 && update(cur + 1));
  const onKey = (e) => {
    if (e.key === 'ArrowRight' && cur < steps.length - 1) update(cur + 1);
    if (e.key === 'ArrowLeft' && cur > 0) update(cur - 1);
  };
  document.addEventListener('keydown', onKey);

  renderMap();
  update(0, false);
  lockCardHeight();

  return {
    resize() {
      renderMap();
      update(cur, false);
      lockCardHeight();
    },
  };
}
